import { Events, track, WizardTrigger } from "apis/procore";
import { log } from "utils/log";

import { getBrowserDisplayName } from "./browserDisplayName";
import { sharedDialogLifecycle } from "./dialogLifecycle";
import { type GuidanceSituation, type GuidanceSnapshot } from "./guidanceModel";

const l = log.analytics;

export type ClassifiedSituation =
    "healthy" | "acceleration-off" | "integrated-gpu" | "discrete-underperforming" | "unknown";

export type WizardLinkItem =
    "gpu-preference" | "hags" | "windowed-games" | "support-article" | "best-practices" | "video";

export function mapSituation(situation: GuidanceSituation | null): ClassifiedSituation {
    switch (situation) {
        case null:
            return "healthy";
        case "A":
            return "acceleration-off";
        case "B":
            return "integrated-gpu";
        case "D":
            return "discrete-underperforming";
        case "E":
            return "unknown";
    }
}

/**
 * `?gpuGuidancePreview=` is a screenshot fixture, not a user flow: it renders the
 * dialog from a synthetic snapshot, so anything it emits is noise. Presence of the
 * param marks the whole session as a debugging session.
 *
 * Deliberately re-reads the param instead of importing `getGuidancePreview()`:
 * `ui/guidancePreview.ts` imports a type from `ui/GuidanceDialog.tsx`, which imports
 * this module, so that route closes a dependency cycle. The type edge is erased at
 * runtime, but `npm run build` runs `skott` over the graph — not worth the risk.
 */
function isPreviewSession(): boolean {
    if (!import.meta.env.DEV || typeof window === "undefined") {
        return false;
    }
    return new URLSearchParams(window.location.search).has("gpuGuidancePreview");
}

/**
 * Single choke point for the four GPU guidance events.
 *
 * `track()` returns early when `procoreContext` carries no company/project id — the
 * case on the standalone `.env.sample` dev config — and that return happens *before*
 * its own `l.info`, so nothing would print locally. Echo the properties here, ahead of
 * the call, so every event stays inspectable in dev regardless of Procore context.
 * Dev-only, so it never reaches a production bundle.
 */
function emit(key: string, properties: Record<string, unknown>): void {
    const preview = isPreviewSession();

    if (import.meta.env.DEV) {
        l.debug(`gpu guidance -> ${key}${preview ? " (preview, not tracked)" : ""}`, properties);
    }

    if (preview) {
        return;
    }

    void track(key, properties);
}

type DegradationMode = "underperformance" | "quality-floor" | "none";

/**
 * Which watchdog trigger is currently in effect. Situations B and E are decided before the
 * breach check in `classifySituation`, so runtime degradation never changes their situation
 * — it is only visible through this property.
 */
function getDegradationMode(snapshot: GuidanceSnapshot): DegradationMode {
    if (snapshot.watchdogBreach) {
        return "underperformance";
    }
    if (snapshot.watchdogQualityFloor) {
        return "quality-floor";
    }
    return "none";
}

let lastTrackedKey: string | undefined;
let trackedNonHealthy = false;

/** Fires on the first snapshot, and whenever the situation or the degradation mode changes. */
export function trackClassified(snapshot: GuidanceSnapshot): void {
    const situation = mapSituation(snapshot.situation);
    const degradationMode = getDegradationMode(snapshot);

    // Keyed on both, so an integrated-GPU (B) or unclassified (E) session that starts
    // underperforming still produces a row even though its situation never moves.
    const key = `${situation}:${degradationMode}`;
    if (key === lastTrackedKey) {
        return;
    }

    // A real fix needs a browser relaunch, so "healthy" after a degraded situation in the
    // same page load is always an artifact: a new watchdog session clears the breach events
    // and situation D falls back to null. Emitting it would read as a fix that never happened.
    // Leave lastTrackedKey as the prior non-healthy key: classified is per page load, so a
    // later identical D after a view/watchdog restart must not fire again.
    if (situation === "healthy" && trackedNonHealthy) {
        return;
    }

    lastTrackedKey = key;
    trackedNonHealthy = trackedNonHealthy || situation !== "healthy";

    const breach = snapshot.watchdogBreach ?? snapshot.watchdogQualityFloor;

    emit(Events.gpuDetection.classified, {
        situation,
        degradation_mode: degradationMode,
        // Window median, not the single tick that crossed the threshold — that sample is
        // explicitly flagged as possibly unrepresentative in WatchdogSnapshot.
        fps_median: breach ? Math.round(breach.snapshot.window.fpsMedian) : null,
        target_fps: breach ? Math.round(breach.snapshot.targetFps) : null,
        renderer: snapshot.renderer ?? snapshot.webgl.renderer ?? "unknown",
        // WebGlProbeResult carries no vendor — WebGPU adapter info is the only vendor source,
        // and situation A skips the WebGPU probe entirely, so "unknown" is expected there.
        gpu_vendor: snapshot.webgpu.adapterInfo?.vendor ?? "unknown",
        // Raw detect-gpu tier, also skipped on the situation A fast path.
        benchmark_tier: snapshot.detectGpuRaw.status === "ok" ? (snapshot.detectGpuRaw.tier ?? null) : null,
        os: getOs(),
        browser: getBrowserDisplayName(),
        surface: snapshot.lifecycle.surface,
        // Lifecycle can still persist `adjusted` from an earlier UI experiment, but there is
        // no "I've adjusted my settings" control anymore, so that value is unused. Dashboards
        // only need Ignore vs everyone else.
        intent: sharedDialogLifecycle.getSnapshot().intent === "ignored" ? "ignored" : "none",
    });
}

export function trackWizardShown(trigger: WizardTrigger, situation: GuidanceSituation | null): void {
    emit(Events.gpuWizard.shown, { trigger, situation: mapSituation(situation) });
}

export function trackWizardDismissed(situation: GuidanceSituation | null): void {
    emit(Events.gpuWizard.dismissed, { situation: mapSituation(situation) });
}

export function trackLinkClicked(item: WizardLinkItem): void {
    emit(Events.gpuWizard.linkClicked, { item });
}

/** Clears the classified dedupe state. Used by tests and `resetGuidanceStore()`. */
export function resetGpuDetectionTelemetry(): void {
    lastTrackedKey = undefined;
    trackedNonHealthy = false;
}

function getOs(): "windows" | "mac" | "other" {
    if (typeof navigator === "undefined") {
        return "other";
    }
    const navigatorLike = navigator as Navigator & { userAgentData?: { platform?: string } };
    const platform = navigatorLike.userAgentData?.platform ?? navigatorLike.platform ?? "";
    const ua = navigatorLike.userAgent;

    if (/mac/i.test(platform) || /macintosh|mac os x/i.test(ua)) {
        return "mac";
    }
    if (/win/i.test(platform) || /windows/i.test(ua)) {
        return "windows";
    }
    return "other";
}
